import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from '../components/icons';
import { Badge, EmptyState, ErrorState, LoadingRows, Modal, PageHeader } from '../components/ui';
import { useToast } from '../context/ToastContext';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errors';
import { formatBytes, formatDateTime } from '../lib/format';
import { qk, useDocs } from '../lib/queries';
import type { Doc } from '../lib/types';

function statusBadge(doc: Doc) {
  if (doc.status === 'ready') return <Badge tone="ok">hazır</Badge>;
  if (doc.status === 'processing') return <Badge tone="warn">işleniyor</Badge>;
  if (doc.status === 'queued') return <Badge>kuyrukta</Badge>;
  return <Badge tone="danger">hata</Badge>;
}

/** S8 — Kütüphane: PDF yükleme, işleme durumu, AI yapılandırma onayı. */
export default function LibraryPage() {
  const docs = useDocs();
  const toast = useToast();
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [confirmDoc, setConfirmDoc] = useState<Doc | null>(null);

  const upload = useMutation({
    mutationFn: (file: File) => api.uploadDoc(file),
    onSuccess: (doc) => {
      toast.push(doc.name + ' yüklendi; işleme alındı.', 'ok');
      void queryClient.invalidateQueries({ queryKey: qk.docs });
    },
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  const reindex = useMutation({
    mutationFn: (id: string) => api.reindexDoc(id),
    onSuccess: () => {
      toast.push('Yeniden indeksleme başladı.', 'ok');
      void queryClient.invalidateQueries({ queryKey: qk.docs });
    },
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteDoc(id),
    onSuccess: () => {
      toast.push('Belge silindi.', 'ok');
      void queryClient.invalidateQueries({ queryKey: qk.docs });
    },
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  const confirmFaqs = useMutation({
    mutationFn: (id: string) => api.confirmDocFaqs(id),
    onSuccess: () => {
      toast.push('SSS adayları onaylandı; bilgi tabanına eklendi.', 'ok');
      setConfirmDoc(null);
      void queryClient.invalidateQueries({ queryKey: qk.docs });
      void queryClient.invalidateQueries({ queryKey: qk.faqs });
    },
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  function handleFiles(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.push('Yalnızca PDF yüklenebilir.', 'error');
      return;
    }
    upload.mutate(file);
  }

  const items = docs.data?.items ?? [];

  return (
    <div>
      <PageHeader
        title="Kütüphane"
        subtitle="PDF belgeleri AI bilgi tabanına eklenir; her belge sayfa/chunk olarak indekslenir."
        actions={
          <button type="button" className="btn btn-primary btn-sm" onClick={() => fileRef.current?.click()} disabled={upload.isPending}>
            <Icon name="upload" size={14} />
            {upload.isPending ? 'Yükleniyor…' : 'PDF yükle'}
          </button>
        }
      />

      <input
        ref={fileRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(event) => {
          handleFiles(event.target.files);
          event.target.value = '';
        }}
      />

      <div className="p-6">
        <div
          className={
            'mb-4 flex flex-col items-center justify-center gap-1 rounded-[8px] border border-dashed px-6 py-6 text-[12px] ' +
            (dragging ? 'border-signal bg-signal-soft text-signal' : 'border-line-strong bg-surface text-muted')
          }
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            handleFiles(event.dataTransfer.files);
          }}
        >
          <Icon name="upload" size={18} />
          <span className="font-medium text-ink">PDF dosyasını buraya bırakın</span>
          <span>veya “PDF yükle” düğmesini kullanın · en fazla 20 MB</span>
        </div>

        {docs.isError ? (
          <ErrorState message={errorMessage(docs.error)} onRetry={() => void docs.refetch()} />
        ) : docs.isLoading ? (
          <LoadingRows rows={5} columns={4} />
        ) : items.length === 0 ? (
          <EmptyState icon="book" title="Kütüphane boş" hint="İade koşulları, garanti şartları gibi belgeleri yükleyin." />
        ) : (
          <div className="panel overflow-hidden">
            <table className="table">
              <caption className="sr-only">Kütüphane belgeleri</caption>
              <thead>
                <tr>
                  <th scope="col">Belge</th>
                  <th scope="col" style={{ width: '110px' }} className="text-right">Boyut</th>
                  <th scope="col" style={{ width: '110px' }} className="text-right">Sayfa</th>
                  <th scope="col" style={{ width: '110px' }} className="text-right">Chunk</th>
                  <th scope="col" style={{ width: '130px' }}>Durum</th>
                  <th scope="col" style={{ width: '150px' }}>Yükleme</th>
                  <th scope="col" style={{ width: '190px' }} />
                </tr>
              </thead>
              <tbody>
                {items.map((doc) => (
                  <tr key={doc.id}>
                    <td>
                      <span className="flex items-center gap-2">
                        <Icon name="file" size={14} className="text-muted" />
                        <span className="max-w-[280px] truncate">{doc.name}</span>
                        {doc.proposedFaqs > 0 ? <Badge tone="signal">{doc.proposedFaqs} SSS adayı</Badge> : null}
                      </span>
                      {doc.error ? <div className="mt-0.5 text-[11px] text-danger">{doc.error}</div> : null}
                    </td>
                    <td className="mono text-right text-[12px]">{formatBytes(doc.sizeBytes)}</td>
                    <td className="mono text-right text-[12px]">{doc.pages || '—'}</td>
                    <td className="mono text-right text-[12px]">{doc.chunks || '—'}</td>
                    <td>{statusBadge(doc)}</td>
                    <td className="mono text-[12px] text-muted">{formatDateTime(doc.uploadedAt)}</td>
                    <td className="text-right">
                      <div className="flex justify-end gap-1">
                        {doc.proposedFaqs > 0 ? (
                          <button type="button" className="btn btn-sm" onClick={() => setConfirmDoc(doc)}>
                            <Icon name="sparkle" size={12} />
                            Onayla
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          aria-label="Yeniden indeksle"
                          title="Yeniden indeksle"
                          disabled={doc.status === 'processing'}
                          onClick={() => reindex.mutate(doc.id)}
                        >
                          <Icon name="refresh" size={14} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm text-danger"
                          aria-label="Sil"
                          title="Sil"
                          onClick={() => {
                            if (window.confirm(doc.name + ' silinsin mi?')) remove.mutate(doc.id);
                          }}
                        >
                          <Icon name="trash" size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="mt-3 text-[11px] leading-4 text-muted">
          İşlenen belgeler AI cevaplarında kaynak olarak gösterilir. OCR gerektiren taranmış PDF'lerde dil paketi (tur) kurulu olmalıdır.
        </p>
      </div>

      <Modal
        open={confirmDoc !== null}
        title="AI yapılandırma önizlemesi"
        wide
        onClose={() => setConfirmDoc(null)}
        footer={
          <>
            <button type="button" className="btn btn-sm" onClick={() => setConfirmDoc(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={confirmFaqs.isPending}
              onClick={() => confirmDoc && confirmFaqs.mutate(confirmDoc.id)}
            >
              SSS adaylarını onayla
            </button>
          </>
        }
      >
        {confirmDoc ? (
          <div className="space-y-3 text-[13px]">
            <div className="flex items-center gap-2">
              <Icon name="file" size={16} className="text-muted" />
              <span className="font-medium">{confirmDoc.name}</span>
              <span className="mono text-[11px] text-muted">
                {confirmDoc.pages} sayfa · {confirmDoc.chunks} chunk
              </span>
            </div>
            <p className="text-[12px] text-muted">
              Belgeden {confirmDoc.proposedFaqs} soru-cevap adayı çıkarıldı. Onayladığınızda SSS tablosuna eklenir ve AI yanıtlarında kullanılır.
            </p>
            <div className="space-y-2">
              <div className="rounded-[4px] border border-line p-3">
                <div className="text-[12px] font-medium">S: Taranmış belge iade için geçerli mi?</div>
                <div className="mt-1 text-[12px] text-ink-2">
                  C: Fatura veya teslim fişi taranmış olsa da iade işlemi için yeterlidir; orijinal belge aranmaz.
                </div>
                <div className="mt-1 text-[11px] text-muted">Kaynak: {confirmDoc.name} s.4</div>
              </div>
              <div className="rounded-[4px] border border-line p-3">
                <div className="text-[12px] font-medium">S: İade kargo ücretini kim öder?</div>
                <div className="mt-1 text-[12px] text-ink-2">C: Ayıplı ürünlerde kargo ücreti satıcıya aittir.</div>
                <div className="mt-1 text-[11px] text-muted">Kaynak: {confirmDoc.name} s.2</div>
              </div>
            </div>
            <p className="text-[11px] text-muted">Onaylamadan önce metinleri SSS ekranından düzenleyebilirsiniz.</p>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
